import { useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '@/app/api';
import { UiButton, UiInput } from '@shared/common/ui';

const moneyFormatter = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const dateTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: 'Europe/Moscow',
});

function formatMoney(value) {
  return moneyFormatter.format(Number(value) || 0);
}

function toIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function currentMonthFilter() {
  const now = new Date();
  return {
    from: toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: toIsoDate(now),
    minAmount: '',
  };
}

/** Успешные платежи всех филиалов за период с суммой не меньше заданной. */
export function ProfilePaymentsWidget({ ownerId }) {
  const [filter, setFilter] = useState(currentMonthFilter);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const search = async (event) => {
    event.preventDefault();
    if (loading) return;
    if (!filter.from || !filter.to) {
      setError('Укажите обе даты периода');
      return;
    }
    if (filter.from > filter.to) {
      setError('Дата начала не может быть позже даты окончания');
      return;
    }
    const minAmount = filter.minAmount === '' ? 0 : Number(filter.minAmount);
    if (Number.isNaN(minAmount) || minAmount < 0) {
      setError('Сумма должна быть неотрицательным числом');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/owner/finance/payments', {
        params: { from: filter.from, to: filter.to, minAmount },
      });
      setResult(data);
    } catch (err) {
      setResult(null);
      setError(err?.response?.data?.message ?? 'Не удалось загрузить платежи');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-card form-card--wide profile-statistics profile-payments">
      <div className="profile-statistics__header">
        <div>
          <h2>Платежи</h2>
          <p className="muted">Успешные платежи всех филиалов</p>
        </div>
      </div>

      <form className="profile-statistics__filters" onSubmit={search}>
        <UiInput
          label="Дата с"
          type="date"
          value={filter.from}
          max={filter.to || undefined}
          onChange={(e) => setFilter((current) => ({ ...current, from: e.target.value }))}
        />
        <UiInput
          label="Дата по"
          type="date"
          value={filter.to}
          min={filter.from || undefined}
          onChange={(e) => setFilter((current) => ({ ...current, to: e.target.value }))}
        />
        <UiInput
          label="Сумма от, ₽"
          type="number"
          min="0"
          step="any"
          placeholder="0"
          value={filter.minAmount}
          onChange={(e) => setFilter((current) => ({ ...current, minAmount: e.target.value }))}
        />
        <div className="profile-statistics__filter-actions">
          <UiButton type="submit" disabled={loading}>
            {loading ? 'Ищем…' : 'Найти'}
          </UiButton>
        </div>
      </form>

      {error ? <div className="form-note form-note--error">{error}</div> : null}

      {result ? (
        <>
          <p className="muted">
            Найдено: {result.total} · на сумму {formatMoney(result.totalAmount)}
            {result.truncated ? ` · показаны первые ${result.items.length}` : ''}
          </p>
          <div className="profile-statistics__table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ФИО</th>
                  <th>Филиал</th>
                  <th>Дата</th>
                  <th style={{ textAlign: 'right' }}>Сумма</th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.clientId && ownerId ? (
                        <Link to={`/${ownerId}/crm/${item.branchId}/clients/${item.clientId}`}>
                          {item.clientFio || 'Без имени'}
                        </Link>
                      ) : (
                        item.clientFio || '—'
                      )}
                    </td>
                    <td>{item.branchName ?? '—'}</td>
                    <td>{dateTimeFormatter.format(new Date(item.soldAt))}</td>
                    <td style={{ textAlign: 'right' }}>{formatMoney(item.amount)}</td>
                  </tr>
                ))}
                {!result.items.length && (
                  <tr>
                    <td colSpan={4} className="muted" style={{ textAlign: 'center' }}>
                      Платежей по заданным условиям нет
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
